import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Cloud, Sun, Moon, Heart, Brain, CheckCircle, Calendar, ArrowRight, Phone } from 'lucide-react';
import CTA from '../components/CTA';

const SeasonalAnxiety: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Seasonal Affective Disorder (SAD) Hypnotherapy | Northern Ireland</title>
        <meta name="description" content="Seasonal affective disorder, winter depression, seasonal anxiety on the Causeway Coast? Hypnotherapy helps manage SAD symptoms, winter darkness, and seasonal work patterns. Serving Portrush, Ballycastle, Bushmills & Northern Ireland." />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/seasonal-anxiety/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with Seasonal Affective Disorder (SAD)?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy can be an effective complementary approach for Seasonal Affective Disorder. It helps regulate mood patterns, improve motivation and energy levels, establish healthier sleep routines, and build resilience to seasonal changes. Hypnotherapy works alongside other SAD treatments like light therapy and vitamin D supplementation, not as a replacement."
                }
              },
              {
                "@type": "Question",
                "name": "How does hypnotherapy differ from treatment for regular depression?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy for seasonal patterns focuses specifically on the cyclical nature of SAD—building anticipatory resilience before winter arrives, managing seasonal triggers (shorter days, darker mornings, weather), and creating mental strategies for the specific months when symptoms worsen. It complements GP care and addresses the predictable seasonal pattern rather than year-round depression."
                }
              },
              {
                "@type": "Question",
                "name": "When should I start treatment for winter SAD?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ideally, start hypnotherapy sessions in September or October—before winter darkness fully sets in. This proactive approach builds mental resilience and coping strategies before symptoms peak (typically November-February in Northern Ireland). Starting early prevents the downward spiral and makes winter more manageable."
                }
              },
              {
                "@type": "Question",
                "name": "Is seasonal anxiety worse on the Causeway Coast?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Coastal locations like Portrush, Portstewart, Ballycastle and Bushmills can experience more dramatic seasonal shifts—darker winter mornings due to northern latitude, dramatic weather changes, and the contrast between busy summer tourism and quiet winter months. These factors can intensify seasonal mood patterns, making proactive mental health support particularly important."
                }
              },
              {
                "@type": "Question",
                "name": "Can you help with seasonal tourism worker stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Causeway Coast tourism workers experience unique seasonal patterns—summer overwork and burnout followed by winter unemployment anxiety and financial stress. Hypnotherapy helps manage the boom-bust cycle, build resilience during off-season, and address the mental health impact of seasonal employment patterns."
                }
              },
              {
                "@type": "Question",
                "name": "What SAD symptoms can hypnotherapy help with?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy may help with common SAD symptoms including low mood and persistent sadness, fatigue and low energy (especially mornings), oversleeping or difficulty waking, increased appetite and carbohydrate cravings, social withdrawal and isolation, loss of interest in activities, difficulty concentrating, and feelings of hopelessness. It works best as part of a comprehensive approach including GP care, light therapy, and lifestyle modifications."
                }
              },
              {
                "@type": "Question",
                "name": "Do I need to see my GP first?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, it's important to consult your GP if you're experiencing significant seasonal depression symptoms. They can rule out other health conditions (thyroid problems, vitamin deficiencies), discuss medication if appropriate, and recommend evidence-based treatments like light therapy. Hypnotherapy then works as a complementary approach alongside your GP's care, not as a replacement."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with sleep patterns affected by seasonal changes?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy can help regulate sleep patterns disrupted by seasonal changes—particularly the difficulty waking in dark winter mornings and the oversleeping common in SAD. It helps establish consistent sleep routines, improve morning wakefulness despite darkness, and reduce the 'hibernation' tendency that worsens winter depression."
                }
              },
              {
                "@type": "Question",
                "name": "What if my symptoms are severe?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "If you're experiencing severe depression symptoms (thoughts of self-harm, inability to function, significant weight changes, complete loss of interest in life), please contact your GP urgently or call Lifeline NI on 0808 808 8000 (24/7 free crisis support). Hypnotherapy is not appropriate for severe clinical depression and you need immediate medical assessment and support."
                }
              },
              {
                "@type": "Question",
                "name": "Is hypnotherapy safe for seasonal mood patterns?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is safe when practiced by a qualified clinical hypnotherapist and used as a complement to GP care. It's particularly effective for mild to moderate seasonal mood changes and for building resilience to prevent symptoms worsening. Always inform your hypnotherapist about any medication you're taking and keep your GP informed about all treatments you're using."
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Seasonal Affective Disorder Hypnotherapy",
            "name": "Seasonal Affective Disorder (SAD) & Seasonal Anxiety Support",
            "description": "Professional solution-focused hypnotherapy for Seasonal Affective Disorder (SAD), winter depression, seasonal anxiety, and seasonal work stress. Complementary to GP care. Serving Portrush, Portstewart, Ballycastle, Bushmills and across Northern Ireland.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Sharon Clare Hypnotherapy",
              "telephone": "07932139245",
              "email": "Sharon@sharonclare-hypnotherapy.com",
              "url": "https://www.sharonclare-hypnotherapy.com/"
            },
            "areaServed": ["Portrush", "Portstewart", "Ballycastle", "Bushmills", "Belfast", "Lisburn", "Northern Ireland"]
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <div className="flex justify-center mb-4">
            <Cloud className="w-12 h-12 text-slate-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">Seasonal Affective Disorder (SAD)</h1>
          <p className="text-xl font-heading text-gray-900 mb-3">Find Light in the Darker Months</p>
          <p className="text-lg sm:text-xl text-gray-600 mb-6">Winter depression, seasonal anxiety, low mood in darker months? Hypnotherapy helps manage Seasonal Affective Disorder (SAD) symptoms and build resilience to seasonal changes across Northern Ireland.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/book" className="btn-primary inline-flex items-center justify-center">
              <Calendar className="w-5 h-5 mr-2" /> Book Free 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" /> Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* SAD Symptoms & Patterns */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Understanding Seasonal Affective Disorder</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Seasonal Affective Disorder (SAD) is more than just "winter blues"—it's a recognized pattern of depression that follows the seasons, typically worsening in autumn and winter when daylight hours shrink dramatically. On the Causeway Coast, the combination of northern latitude, coastal weather, and dramatic seasonal tourism shifts can intensify these patterns.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <Moon className="w-5 h-5 text-indigo-600 mr-2" />
                Winter SAD Symptoms (Nov-Feb Peak)
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Persistent low mood, sadness, or feeling "flat"</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Extreme fatigue despite adequate sleep</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Difficulty waking in dark mornings</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Oversleeping or "hibernation" tendency</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Increased appetite, especially carbohydrate cravings</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Social withdrawal and isolation</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Loss of interest in usual activities</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Difficulty concentrating or making decisions</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center">
                <Cloud className="w-5 h-5 text-slate-600 mr-2" />
                Northern Ireland Seasonal Triggers
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Northern latitude: earlier sunsets, limited daylight hours (Dec: ~7.5 hours daylight)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Coastal weather: dramatic storms, grey skies, persistent rain</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Tourism industry seasonal unemployment (Portrush, Portstewart, Bushmills)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Contrast: vibrant summer crowds vs isolated winter quiet</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Fishing/maritime work seasonal patterns (Ballycastle)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Financial stress during off-season months</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Social isolation in quieter winter coastal communities</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h3 className="font-heading font-semibold text-gray-900 mb-2 flex items-center">
              <Sun className="w-5 h-5 text-amber-600 mr-2" />
              Summer SAD (Less Common But Real)
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              A small percentage of people experience <strong>summer-pattern SAD</strong> (May-August), often characterized by anxiety, agitation, insomnia, reduced appetite, and irritability. On the Causeway Coast, summer tourism industry overwork, extended daylight hours disrupting sleep, and financial pressure during peak season can trigger or worsen summer seasonal patterns. If you experience mood changes during summer rather than winter, hypnotherapy can help with this pattern too.
            </p>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Hypnotherapy Supports Seasonal Wellbeing</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Solution-focused hypnotherapy doesn't "cure" Seasonal Affective Disorder—it works <em>alongside</em> other treatments (GP care, light therapy, vitamin D, exercise) to help you build mental resilience to seasonal changes, manage mood symptoms, and maintain motivation during darker months.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-3">
                <Brain className="w-6 h-6 text-blue-600" />
                <h3 className="font-heading font-semibold text-gray-900">Regulate Mood Patterns</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Hypnotherapy helps stabilize mood fluctuations by working with your brain's natural regulation systems. It can reduce the severity of seasonal dips, build anticipatory resilience before winter arrives, and create mental strategies for managing darker months more effectively.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-3">
                <Moon className="w-6 h-6 text-indigo-600" />
                <h3 className="font-heading font-semibold text-gray-900">Improve Sleep Quality</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Address the sleep disruption common in SAD—difficulty waking in dark mornings, oversleeping, or "hibernation" patterns. Hypnotherapy helps establish healthier sleep routines, improve morning wakefulness despite darkness, and regulate circadian rhythms disrupted by seasonal changes.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-3">
                <Heart className="w-6 h-6 text-rose-600" />
                <h3 className="font-heading font-semibold text-gray-900">Build Motivation & Energy</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Combat the lethargy and loss of interest common in SAD. Hypnotherapy helps rebuild motivation for activities, reduces social withdrawal, and creates mental strategies to maintain engagement with life even when seasonal depression makes everything feel harder.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="font-heading font-semibold text-gray-900 mb-4">Proactive Seasonal Support</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Early Intervention (Sept-Oct)</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Start sessions <strong>before</strong> winter darkness fully sets in</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Build mental resilience and coping strategies in advance</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Establish sleep routines before winter disruption</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Create action plan for darker months ahead</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Winter Maintenance (Nov-Feb)</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Ongoing sessions to maintain mood stability</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Address specific symptoms as they arise (fatigue, withdrawal)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Reinforce motivation and energy levels</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Prevent symptom escalation during darkest months</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Complementary Approaches */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Comprehensive Seasonal Wellbeing</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Hypnotherapy works best as part of a comprehensive approach to Seasonal Affective Disorder. The most effective SAD management combines multiple evidence-based strategies:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Medical & Physical Approaches</h3>
              <ul className="space-y-3 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>GP Consultation:</strong> Rule out thyroid problems, vitamin deficiencies, discuss medication if needed
                  </div>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Light Therapy:</strong> 10,000 lux light box for 30 mins each morning (evidence-based SAD treatment)
                  </div>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Vitamin D Supplementation:</strong> 1,000-2,000 IU daily (Northern Ireland winter sunlight insufficient)
                  </div>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Regular Exercise:</strong> 30 mins daily outdoor activity when possible (natural light exposure + endorphins)
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Lifestyle & Mental Approaches</h3>
              <ul className="space-y-3 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Hypnotherapy:</strong> Mental resilience, mood regulation, motivation, sleep quality
                  </div>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Consistent Sleep Schedule:</strong> Same wake time even in dark mornings; resist oversleeping urge
                  </div>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Social Connection:</strong> Resist isolation tendency; schedule regular social activities
                  </div>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Structure & Routine:</strong> Maintain activities even when motivation low; structure prevents spiral
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-rose-50 border border-rose-200 rounded-xl p-6">
            <h3 className="font-heading font-semibold text-gray-900 mb-2 flex items-center">
              <Phone className="w-5 h-5 text-rose-600 mr-2" />
              When to Seek Immediate Help
            </h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you're experiencing severe symptoms—thoughts of self-harm or suicide, inability to function in daily life, significant weight loss/gain, complete loss of interest in everything—please contact your GP urgently or call:
            </p>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Lifeline NI:</strong> 0808 808 8000 (24/7 free crisis support)
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Samaritans:</strong> 116 123 (24/7 listening service)
                </div>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Emergency:</strong> 999 or attend A&E if in immediate crisis
                </div>
              </li>
            </ul>
            <p className="text-gray-700 text-sm mt-3">
              <strong>Hypnotherapy is not appropriate for severe clinical depression.</strong> It's a complementary approach for mild to moderate seasonal mood changes alongside GP care.
            </p>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Sharon's Approach to Seasonal Wellbeing</h2>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                As a solution-focused clinical hypnotherapist, Sharon understands that Seasonal Affective Disorder requires a comprehensive, proactive approach. Hypnotherapy sessions focus on building mental resilience <em>before</em> symptoms peak, creating sustainable coping strategies for darker months, and maintaining motivation when seasonal depression makes everything feel harder.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Sharon emphasizes the importance of GP collaboration—hypnotherapy complements (not replaces) medical care, light therapy, and vitamin D supplementation. Sessions address the specific challenges of SAD: difficulty waking in dark mornings, oversleeping and "hibernation" patterns, social withdrawal, carbohydrate cravings, and loss of interest in activities.
              </p>
              <p className="text-gray-700 leading-relaxed">
                For Causeway Coast clients, Sharon understands the unique seasonal challenges—dramatic winter darkness at northern latitude, tourism industry seasonal unemployment stress, and the contrast between vibrant summer and isolated winter in coastal communities. In-person sessions are available at her <strong>Belfast clinic on the Ormeau Road</strong> and at <strong>The Arzen Collective in Lisburn</strong>, or online for maximum convenience during darker months when travel feels more difficult.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Proactive Seasonal Timeline</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">September-October:</strong>
                    <p className="text-gray-700 text-sm">Begin sessions before winter arrives. Build resilience, establish routines, prepare mentally for darker months ahead.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">November-February:</strong>
                    <p className="text-gray-700 text-sm">Ongoing support during peak SAD months. Maintain mood stability, address symptoms, prevent escalation.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">March-April:</strong>
                    <p className="text-gray-700 text-sm">Transition support as days lengthen. Consolidate progress, plan for next winter's proactive start.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Causeway Coast Context */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Seasonal Support on the Causeway Coast</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Coastal communities in Northern Ireland experience particularly dramatic seasonal shifts—from bustling summer tourism to quiet winter months, vibrant long summer days to dark winter mornings. These extremes can intensify Seasonal Affective Disorder patterns.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <a href="/portrush-hypnotherapy" className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Portrush</h3>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Events capital seasonal contrast:</strong> NW200/Open Championship summer buzz vs winter quiet. Tourism industry seasonal unemployment stress. Winter darkness particularly stark after vibrant summer energy. Online sessions recommended (20 mi).
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/portstewart-hypnotherapy" className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Portstewart</h3>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Genteel resort seasonal rhythm:</strong> Retirement community winter isolation after summer sociability. Coastal wellness culture embraces mental health support. Northern latitude winter darkness pronounced. Online sessions (19 mi).
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/ballycastle-hypnotherapy" className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Ballycastle</h3>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Working coastal town patterns:</strong> Fishing/ferry seasonal work stress. Winter unemployment anxiety for maritime workers. Tourism seasonal employment boom-bust cycle. Coastal weather particularly harsh. Online sessions (18 mi).
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/bushmills-hypnotherapy" className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Bushmills</h3>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Heritage tourism village:</strong> Distillery tours seasonal staffing patterns. Winter visitor drought after summer crowds. Small village social isolation in darker months. Year-round distillery work helps but tourism roles seasonal. Online sessions (15 mi).
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>
          </div>

          <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
            <h3 className="font-heading font-semibold text-gray-900 mb-3">Clinic &amp; Online Sessions</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Sharon sees clients in person at her <strong>Belfast clinic on the Ormeau Road</strong> and at <strong>The Arzen Collective in Lisburn</strong>, with early evening appointments available.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Online sessions are also available and particularly beneficial during darker months when SAD symptoms make travel feel overwhelming. Access support from the comfort of your home, avoid winter driving in coastal weather, and maintain consistency even when motivation is low.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Can hypnotherapy help with Seasonal Affective Disorder (SAD)?</h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, hypnotherapy can be an effective complementary approach for Seasonal Affective Disorder. It helps regulate mood patterns, improve motivation and energy levels, establish healthier sleep routines, and build resilience to seasonal changes. Hypnotherapy works alongside other SAD treatments like light therapy and vitamin D supplementation, not as a replacement.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">When should I start treatment for winter SAD?</h3>
              <p className="text-gray-700 leading-relaxed">
                Ideally, start hypnotherapy sessions in September or October—before winter darkness fully sets in. This proactive approach builds mental resilience and coping strategies before symptoms peak (typically November-February in Northern Ireland). Starting early prevents the downward spiral and makes winter more manageable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Can you help with seasonal tourism worker stress?</h3>
              <p className="text-gray-700 leading-relaxed">
                Absolutely. Causeway Coast tourism workers experience unique seasonal patterns—summer overwork and burnout followed by winter unemployment anxiety and financial stress. Hypnotherapy helps manage the boom-bust cycle, build resilience during off-season, and address the mental health impact of seasonal employment patterns.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Do I need to see my GP first?</h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, it's important to consult your GP if you're experiencing significant seasonal depression symptoms. They can rule out other health conditions (thyroid problems, vitamin deficiencies), discuss medication if appropriate, and recommend evidence-based treatments like light therapy. Hypnotherapy then works as a complementary approach alongside your GP's care, not as a replacement.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">What if my symptoms are severe?</h3>
              <p className="text-gray-700 leading-relaxed">
                If you're experiencing severe depression symptoms (thoughts of self-harm, inability to function, significant weight changes, complete loss of interest in life), please contact your GP urgently or call Lifeline NI on 0808 808 8000 (24/7 free crisis support). Hypnotherapy is not appropriate for severe clinical depression and you need immediate medical assessment and support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/anxiety-stress" className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">General Anxiety & Stress</h3>
              <p className="text-sm text-gray-700 mb-3">
                Year-round anxiety treatment including social anxiety, work stress, and panic attacks.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/burnout-recovery" className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Burnout Recovery</h3>
              <p className="text-sm text-gray-700 mb-3">
                Support for exhaustion, overwork, and recovery from seasonal employment burnout patterns.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>

            <a href="/holiday-anxiety" className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <h3 className="font-heading font-semibold text-gray-900 mb-2">Holiday & Travel Anxiety</h3>
              <p className="text-sm text-gray-700 mb-3">
                Pre-trip anxiety, travel stress, and holiday season overwhelm management.
              </p>
              <span className="text-blue-600 text-sm font-medium">Learn more →</span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Build Resilience for Seasonal Changes"
        subtitle="Don't wait until winter darkness fully sets in. Start building mental resilience now with proactive hypnotherapy support. Work alongside your GP care and other SAD treatments to make this winter more manageable."
        primaryButtonText="Book Free 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default SeasonalAnxiety;
